import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog'
import { Description, Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useEquipmentActivities, type EquipmentActivityInput } from '../lib/farm-level-logs'
import { byDateDesc, FarmLogLayout, orNull, text, useSavedEntries, type DialogState } from '../lib/farm-log-layout'
import { useLogNames } from '../lib/farm-log-names'
import { AssetNameInput } from '../lib/asset-picker'
import { assetIdFor, useFarmAssets } from '../lib/inventory'
import type { Asset, EquipmentActivity, EquipmentActivityType } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Equipment Cleaning, Maintenance and Calibration'

/** Form I activity codes, numbered as on the form. */
const ACTIVITIES: { value: EquipmentActivityType; label: string }[] = [
  { value: 'calibration', label: '1 – Calibration' },
  { value: 'maintenance', label: '2 – Maintenance' },
  { value: 'repair', label: '3 – Repair' },
  { value: 'cleaning', label: '4 – Cleaning' },
  { value: 'inspection', label: '5 – Inspection' },
  { value: 'other', label: '6 – Other (specify)' },
]
const activityLabel = (type: EquipmentActivityType) => ACTIVITIES.find((a) => a.value === type)!.label.replace(/^\d – /, '').replace(' (specify)', '')

function EquipmentActivityDialog({
  onClose,
  initial,
  equipment,
  assets,
  onSave,
}: {
  onClose: () => void
  initial?: EquipmentActivity
  equipment: string[]
  /** Inventory equipment usable at this farm. */
  assets: Asset[]
  onSave: (input: EquipmentActivityInput) => Promise<unknown>
}) {
  const [performedOn, setPerformedOn] = useState(initial?.performedOn ?? '')
  const [equipmentName, setEquipmentName] = useState(initial?.equipmentName ?? '')
  const [activityType, setActivityType] = useState<EquipmentActivityType>(initial?.activityType ?? 'cleaning')
  const [description, setDescription] = useState(text(initial?.description))
  const [performedBy, setPerformedBy] = useState(text(initial?.performedBy))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const assetId = assetIdFor(assets, equipmentName, initial && { name: initial.equipmentName, assetId: initial.assetId })
  const check = useEntryCheck(org?.id, farmId, { form: 'i', entry: { performedOn: performedOn || null, equipmentName: orNull(equipmentName), activityType, description: orNull(description), performedBy: orNull(performedBy) } })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!performedOn || !equipmentName.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({ performedOn, equipmentName: equipmentName.trim(), assetId, activityType, description: orNull(description), performedBy: orNull(performedBy) })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save activity.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{initial ? 'Edit equipment activity' : 'Log equipment activity'}</DialogTitle>
      <DialogDescription>Production site and building equipment, plus water tank/cistern and packaging material cleaning.</DialogDescription>
      <form onSubmit={handleSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="I" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField>
                <Label>Date</Label>
                <Input type="date" value={performedOn} onChange={(e) => setPerformedOn(e.target.value)} required autoFocus />
              </FormField>
              <FormField>
                <Label>Activity code</Label>
                <Select value={activityType} onChange={(e) => setActivityType(e.target.value as EquipmentActivityType)}>
                  {ACTIVITIES.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <FormField>
              <Label>Equipment activity performed on</Label>
              <AssetNameInput
                assets={assets}
                pastNames={equipment}
                linkedAssetId={assetId}
                inventoryHref={`/farms/${farmId}/inventory`}
                value={equipmentName}
                onChange={setEquipmentName}
                placeholder="e.g. Airblast sprayer"
                required
              />
            </FormField>
            <FormField>
              <Label>Brief description of activity</Label>
              <Description>{activityType === 'other' ? 'Say what the activity was, and why and how.' : 'Why and how it was done.'}</Description>
              <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              <InputWarning result={check} input="description" />
            </FormField>
            <FormField>
              <Label>Employee completing job</Label>
              <Input value={performedBy} onChange={(e) => setPerformedBy(e.target.value)} />
              <InputWarning result={check} input="performedBy" />
            </FormField>
          </FieldGroup>
          <EntryCheckPanel result={check} formCode="I" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="I" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function EquipmentActivitiesPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [equipment, setEquipment] = useState('')
  const [dialog, setDialog] = useState<DialogState<EquipmentActivity>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLog, updateLog } = useEquipmentActivities(org?.id, farmId, year, equipment || undefined)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)
  const { names, reload: reloadNames } = useLogNames(org?.id, farmId, 'equipment')
  const { assets } = useFarmAssets(org?.id, farmId, 'EQUIPMENT')

  async function save(input: EquipmentActivityInput) {
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog(input)
    await Promise.all([reloadStatus(), reloadNames()])
    if (log) saved.setIds([log.id])
  }

  const entries = [...logs].sort(byDateDesc((r) => r.performedOn)).map((log) => ({
    key: log.id,
    date: log.performedOn,
    onClick: () => setDialog({ log }),
    content: (
      <>
        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
          {log.equipmentName} <span className="text-zinc-400 dark:text-zinc-500">· {activityLabel(log.activityType)}</span>
          {log.assetId && (
            <Badge color="zinc" className="ml-2">
              Inventory
            </Badge>
          )}
        </p>
        {log.description && <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{log.description}</p>}
        {log.performedBy && <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{log.performedBy}</p>}
        <EntryIssues issue={issueFor(status?.forms.i, log.id)} />
      </>
    ),
  }))

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="i"
      title="Equipment cleaning, maintenance and calibration"
      formName={FORM_NAME}
      addLabel="Log activity"
      addHref={`/farms/${farmId}/log/equipment`}
      year={year}
      onYearChange={setYear}
      nameFilter={{ label: 'Equipment', options: names, value: equipment, onChange: setEquipment }}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No equipment activities in ${year}.`}
      saved={saved}
    >
      {dialog && <EquipmentActivityDialog key={dialog.log?.id ?? 'new'} onClose={() => setDialog(null)} initial={dialog.log} equipment={names} assets={assets} onSave={save} />}
    </FarmLogLayout>
  )
}
